import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { I18nService } from '../../core/i18n.service';
import { PageDataService } from '../../core/page-data.service';
import { SiteConfigService } from '../../core/site-config.service';

export interface BlogItem {
  id: number;
  slug: string;
  title: string;
  preview: string;
  markdown?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  canonicalUrl?: string | null;
  ogTitle?: string | null;
  ogImageUrl?: string | null;
  imageUrl?: string | null;
  imageSizes?: Record<string, unknown> | unknown[] | null;
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  readingMinute?: number | null;
  cta?: BlogCta | null;
}

export interface BlogCta {
  type: 'category' | 'listing';
  targetID: number;
  title: string;
  image?: string | null;
  url: string;
}

export interface BlogListResponse {
  items: BlogItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

@Injectable({ providedIn: 'root' })
export class BlogService {
  private readonly api = inject(ApiClientService);
  private readonly i18n = inject(I18nService);
  private readonly pageData = inject(PageDataService);
  private readonly site = inject(SiteConfigService);

  async list(page = 1, limit = 12, useSeeded = true): Promise<BlogListResponse> {
    await this.site.ready();
    if (useSeeded) {
      const seeded = this.seededList(page, limit);
      if (seeded) {
        return seeded;
      }
    }
    if (typeof window !== 'undefined') console.info('[blog-api] POST /get/blogs', { page, limit, useSeeded, lang: this.i18n.lang() });

    const response = await firstValueFrom(this.api.post<{ data: BlogListResponse }>('blogs', {
      page,
      limit,
      ...this.localePayload()
    }));
    if (typeof window !== 'undefined') console.info('[blog-api] response /get/blogs', response);

    return this.normalizeListResponse(response.data, page, limit);
  }

  async detail(slug: string): Promise<BlogItem | null> {
    await this.site.ready();
    const seededResponse = this.pageData.get<{ data?: { item?: BlogItem } }>('blogDetail');
    if (seededResponse?.data?.item?.slug === slug) {
      return this.normalizeItem(seededResponse.data.item);
    }

    const response = await firstValueFrom(this.api.post<{ data: { item: BlogItem } }>('blogDetail', {
      slug,
      ...this.localePayload()
    }));

    return response.data.item ? this.normalizeItem(response.data.item) : null;
  }

  private seededList(page: number, limit: number): BlogListResponse | null {
    const key = limit <= 8 ? 'homeBlogs' : 'blogList';
    const seededResponse = this.pageData.get<{ data?: BlogListResponse }>(key);
    if (!seededResponse?.data?.items) {
      return null;
    }

    const normalized=this.normalizeListResponse(seededResponse.data, page, limit);
    return normalized.page===page ? normalized : null;
  }

  private normalizeListResponse(value: Partial<BlogListResponse>, requestedPage: number, requestedLimit: number): BlogListResponse {
    const page = Math.max(1, Number(value.page ?? requestedPage));
    const limit = Math.max(1, Number(value.limit ?? requestedLimit));
    const items = Array.isArray(value.items) ? value.items.map((item) => this.normalizeItem(item)) : [];
    const total = Math.max(0, Number(value.total ?? 0));
    const hasMore = total > 0
      ? page * limit < total
      : items.length >= limit && Boolean(value.hasMore);
    return { items, page, limit, total, hasMore };
  }

  normalizeItem(item: BlogItem): BlogItem {
    return {
      ...item,
      imageUrl: this.absoluteMediaUrl(item.imageUrl),
      imageSizes: item.imageSizes ?? null,
      cta: item.cta ? {
        ...item.cta,
        image: this.absoluteMediaUrl(item.cta.image)?.replace('[size]', 's') ?? null
      } : null
    };
  }

  private localePayload(): { lang: string; country: string; locale: string } {
    return {
      lang: this.i18n.lang(),
      country: this.i18n.country(),
      locale: this.i18n.locale()
    };
  }

  private absoluteMediaUrl(url?: string | null): string | null {
    if (!url) {
      return null;
    }
    if (/^https?:\/\//.test(url)) {
      return url;
    }

    return `${this.site.config().apiBaseUrl}/${url.replace(/^\/+/, '')}`;
  }
}
